/**
 * Admin Package Management Constants
 */

/**
 * Package codes that are protected from deletion and deactivation.
 * These packages are critical to core system operations (e.g., default assignment on registration/downgrade).
 */
export const PROTECTED_PACKAGE_CODES: readonly string[] = ['starter', 'owner'];

/**
 * Helper to check if a package code is protected
 */
export const isProtectedPackage = (packageCode: string): boolean =>
  PROTECTED_PACKAGE_CODES.includes(packageCode);

export const EXPIRY_PERIODS = [
  '1 month',
  '3 months',
  '6 months',
  '1 year',
  'never',
] as const;

export const PERMISSION_PRESETS = {
  starter: [
    // Basic UI access - No API
    'slip:view',
    'slip:verify',
    'dashboard:view',
    'branch:view',
    'accounts:view',
    'team:view',
    'reports:view',
    'reports:export',
    'billing:view',
    'credit:purchase',
  ],
  basic: [
    // Basic + API platform access
    'api:use',      // API platform access (view docs, manage keys, QR generation)
    'slip:view',
    'slip:verify',
    'dashboard:view',
    'branch:view',
    'accounts:view',
    'team:view',
    'reports:view',
    'reports:export',
    'billing:view',
    'credit:purchase',
  ],
  premium: [
    // Premium features with API verification + business logic + history
    'api:use',      // API platform access
    'api:verify',   // Verification endpoints
    'api:view',     // View API history and usage stats
    'slip:view',
    'slip:verify',
    'dashboard:view',
    'branch:view',
    'accounts:view',
    'team:view',
    'reports:view',
    'billing:view',
  ],
  enterprise: [
    // Full API + advanced features + management capabilities
    'api:manage',   // Full API management (regenerate, delete, whitelist)
    'api:use',
    'api:verify',
    'api:view',
    'slip:view',
    'slip:verify',
    'dashboard:view',
    'branch:view',
    'accounts:view',
    'team:view',
    'reports:view',
    'reports:export',
    'billing:view',
    'force-mode:use',
  ],
  owner: [
    // Complete system access (21 permissions - removed api:generate)
    'api:manage',
    'api:use',
    'api:verify',
    'api:view',
    'slip:view',
    'slip:verify',
    'dashboard:view',
    'branch:view',
    'accounts:view',
    'team:view',
    'reports:view',
    'reports:export',
    'billing:view',
    'credit:purchase',
    'force-mode:use',
  ],
} as const;

export const FEATURE_TEMPLATES = {
  starter: [
    'Basic slip verification',
    'Email support',
    'Community access',
  ],
  basic: [
    'Basic slip verification',
    'Multi-bank support',
    'Email support',
    'API access',
  ],
  premium: [
    'Advanced slip verification',
    'Multi-bank support',
    'Webhook notifications',
    'Priority email support',
    'Analytics dashboard',
    'API access',
    'History tracking',
  ],
  enterprise: [
    'All Premium features',
    'Custom integrations',
    'SLA guarantee',
    'Dedicated support',
    'Force refresh mode',
    'Advanced analytics',
    'Multi-branch support',
    'Team management',
  ],
  owner: [
    'All Enterprise features',
    'Complete system access',
    'Unlimited API calls',
    'White-label options',
    'Priority development',
    'Custom development',
    'On-premise deployment',
    'Advanced security features',
  ],
} as const;

// Package-Level Permissions Only
// API features and business capabilities tied to subscription packages
// Excluded: UI access (dashboard:*, settings:*), Team management (team:*, customer:*), Admin (admin:*, system:*)
export const AVAILABLE_PERMISSIONS = [
  { 
    category: 'API Connect', 
    permissions: [
      'api:use',      // Platform access (view docs, manage keys, QR generation, API v3 endpoints)
      'api:verify',   // Verification endpoints (verify slips via API)
      'api:view',     // View API history/logs and usage statistics
      'api:manage',   // Full API key management (regenerate, delete, whitelist)
    ] 
  },
  { category: 'Slip', permissions: ['slip:view', 'slip:verify'] },
  { category: 'Dashboard', permissions: ['dashboard:view'] },
  { category: 'Branch', permissions: ['branch:view'] },
  { category: 'Accounts', permissions: ['accounts:view'] },
  { category: 'Team', permissions: ['team:view'] },
  { category: 'Reports', permissions: ['reports:view', 'reports:export'] },
  { category: 'Billing', permissions: ['billing:view', 'credit:purchase'] },
  { category: 'Force Mode', permissions: ['force-mode:use'] },
] as const;

// Permission descriptions for tooltips
export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  'api:use': 'เข้าถึง API platform (ดูเอกสาร, จัดการ API keys, QR generation, เรียกใช้ API v3)',
  'api:verify': 'เรียกใช้ verification endpoints สำหรับตรวจสอบสลิปผ่าน API',
  'api:view': 'ดูประวัติการใช้งาน API, usage statistics และ logs',
  'api:manage': 'จัดการ API keys แบบเต็มรูปแบบ (regenerate, delete, IP whitelist)',
  'slip:view': 'ดูข้อมูลสลิป, รายการสลิป, และประวัติการตรวจสอบ',
  'slip:verify': 'ตรวจสอบสลิปผ่าน UI',
  'dashboard:view': 'เข้าถึง dashboard หลัก',
  'branch:view': 'ดูข้อมูลสาขา',
  'accounts:view': 'ดูบัญชีธนาคาร',
  'team:view': 'ดูข้อมูลทีมและสมาชิก',
  'reports:view': 'ดูรายงานต่างๆ',
  'reports:export': 'ส่งออกรายงานเป็นไฟล์',
  'billing:view': 'ดูข้อมูลการเรียกเก็บเงินและ invoice',
  'credit:purchase': 'ซื้อเครดิตเพิ่ม',
  'force-mode:use': 'ใช้ force refresh mode สำหรับการตรวจสอบแบบเร็ว',
};

export const DEFAULT_RATE_LIMITS = {
  starter: {
    monthlyRequests: 1000,
    dailyRequests: 50,
    ratePerMinute: 10,
    burstLimit: 20,
  },
  basic: {
    monthlyRequests: 5000,
    dailyRequests: 200,
    ratePerMinute: 30,
    burstLimit: 60,
  },
  premium: {
    monthlyRequests: 25000,
    dailyRequests: 1000,
    ratePerMinute: 100,
    burstLimit: 200,
  },
  enterprise: {
    monthlyRequests: -1, // unlimited
    dailyRequests: -1,
    ratePerMinute: 500,
    burstLimit: 1000,
  },
} as const;

export const PACKAGE_COLORS = {
  starter: 'bg-gray-100 text-gray-800 border-gray-300',
  basic: 'bg-blue-100 text-blue-800 border-blue-300',
  premium: 'bg-purple-100 text-purple-800 border-purple-300',
  enterprise: 'bg-orange-100 text-orange-800 border-orange-300',
  owner: 'bg-green-100 text-green-800 border-green-300',
} as const;
